// src/pages/RefundStatusPage.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../utils/api";
import { API_CONFIG } from '../utils/apiConfig';
import HeaderLanding from "../components/partials/HeaderLanding";
import SmartsuppChat from "../components/external/SmartsuppChat";

const RefundStatusPage = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();

  const [refundData, setRefundData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchCaseId, setSearchCaseId] = useState(caseId || "");

  useEffect(() => {
    if (caseId) {
      fetchRefundStatus(caseId);
    }
  }, [caseId]);

  const fetchRefundStatus = async (id) => {
    if (!id.trim()) {
      setError("Please enter a case ID");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await api.get(`${API_CONFIG.REFUNDS.REFUND_STATUS}/${id}`);

      if (response.data.success) {
        setRefundData(response.data.refundRequest);
        if (id !== caseId) {
          navigate(`/refund-status/${id}`, { replace: true });
        }
      } else {
        setError(response.data.message || "Refund request not found");
        setRefundData(null);
      }
    } catch (error) {
      console.error("Error fetching refund status:", error);
      setError(error.response?.data?.message || "Failed to fetch refund status");
      setRefundData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRefundStatus(searchCaseId);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
      case 'processed':
        return "bg-green-100 text-green-800 border-green-200";
      case 'pending':
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case 'under_review':
        return "bg-blue-100 text-blue-800 border-blue-200";
      case 'rejected':
      case 'cancelled':
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100  border-gray-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return "✓";
      case 'processed':
        return "✅";
      case 'pending':
        return "⏳";
      case 'under_review':
        return "🔍";
      case 'rejected':
        return "❌";
      case 'cancelled':
        return "🚫";
      default:
        return "❓";
    }
  };

  const formatCurrency = (amount, currency = 'NGN') => {
    if (!amount) return currency === 'NGN' ? '₦0.00' : '$0.00';
    return new Intl.NumberFormat(currency === 'NGN' ? 'en-NG' : 'en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getRefundTypeLabel = (type) => {
    switch (type) {
      case 'full': return 'Full Refund';
      case 'partial': return 'Partial Refund';
      case 'shipping': return 'Shipping Refund';
      default: return type;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <HeaderLanding />

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold  mb-2">Check Refund Status</h1>
          <p className="text-gray-600">
            Enter your case ID to check the current status of your refund request
          </p>
        </div>

        {/* Search Form */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                value={searchCaseId}
                onChange={(e) => setSearchCaseId(e.target.value)}
                placeholder="Enter your case ID (e.g., REF-123456789-ABC123)"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={isLoading}
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Searching...
                </>
              ) : (
                "Check Status"
              )}
            </button>
          </form>

          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700">{error}</p>
            </div>
          )}
        </div>

        {/* Refund Status Display */}
        {refundData && (
          <div className="space-y-6">
            {/* Main Status Card */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-xl font-bold ">
                    Case #{refundData.case_id}
                  </h2>
                  <p className="text-gray-600">Submitted on {formatDate(refundData.created_at)}</p>
                  <p className="text-gray-600">Order: {refundData.order_id}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-4 py-2 rounded-full text-sm font-medium border ${getStatusColor(refundData.status)}`}>
                    {getStatusIcon(refundData.status)} {refundData.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
              </div>

              {/* Processing Info */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-semibold text-blue-800 mb-2">Processing Information</h3>
                  <p className="text-blue-700 text-sm">
                    <strong>Days since submission:</strong> {refundData.processingInfo?.daysSinceSubmission || 0}
                  </p>
                  <p className="text-blue-700 text-sm">
                    <strong>Estimated completion:</strong> {refundData.processingInfo?.estimatedCompletion || 'Under review'}
                  </p>
                </div>

                <div className="p-4 bg-green-50 rounded-lg">
                  <h3 className="font-semibold text-green-800 mb-2">Refund Details</h3>
                  <p className="text-green-700 text-sm">
                    <strong>Type:</strong> {getRefundTypeLabel(refundData.refund_type)}
                  </p>
                  <p className="text-green-700 text-sm">
                    <strong>Requested:</strong> {formatCurrency(refundData.requested_amount)}
                  </p>
                  {refundData.processed_amount && (
                    <p className="text-green-700 text-sm">
                      <strong>Processed:</strong> {formatCurrency(refundData.processed_amount)}
                    </p>
                  )}
                </div>
              </div>

              {/* Reason and Description */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-700 mb-3">Refund Details</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Reason</label>
                    <p className="">{refundData.reason.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Description</label>
                    <p className=" whitespace-pre-wrap">{refundData.description}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Status History */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-semibold text-gray-700 mb-4">Status History</h3>
              <div className="space-y-4">
                {refundData.statusHistory && refundData.statusHistory.length > 0 ? (
                  refundData.statusHistory.map((update, index) => (
                    <div key={index} className="flex items-start gap-4 p-4 border border-gray-200 rounded-lg">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${getStatusColor(update.status)}`}>
                        {getStatusIcon(update.status)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(update.status)}`}>
                            {update.status.replace('_', ' ').toUpperCase()}
                          </span>
                          <span className="text-sm text-gray-500">{formatDate(update.created_at)}</span>
                        </div>
                        <p className=" text-sm mb-2">{update.notes}</p>
                        {update.admin_notes && (
                          <div className="bg-white p-3 rounded">
                            <p className="text-xs text-gray-600 font-medium mb-1">Admin Notes:</p>
                            <p className="text-xs text-gray-600">{update.admin_notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">No status history available</p>
                )}
              </div>
            </div>

            {/* Evidence Files */}
            {refundData.evidence && refundData.evidence.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="font-semibold text-gray-700 mb-4">Supporting Evidence</h3>
                <div className="grid gap-3">
                  {refundData.evidence.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                          {file.file_type?.startsWith('image/') ? (
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium ">{file.file_name}</p>
                          <p className="text-xs text-gray-500">{formatFileSize(file.file_size)} • {formatDate(file.uploaded_at)}</p>
                        </div>
                      </div>
                      <a
                        href={file.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                      >
                        View
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Information */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-semibold text-gray-700 mb-4">Contact Information</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Contact Details</h4>
                  <p className="">{refundData.contact_email}</p>
                  {refundData.contact_phone && (
                    <p className="">{refundData.contact_phone}</p>
                  )}
                  <p className="text-sm text-gray-600 mt-2">
                    Preferred contact: {refundData.preferred_contact}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Order Information</h4>
                  <p className="">Order ID: {refundData.order_id}</p>
                  <p className="">Order Type: {refundData.order_type || 'Not specified'}</p>
                  <p className="">Original Amount: {formatCurrency(refundData.original_order_amount)}</p>
                </div>
              </div>
            </div>

            {/* Support Information */}
            <div className="bg-blue-50 rounded-xl p-6">
              <h3 className="font-semibold text-blue-800 mb-2">Need Help?</h3>
              <p className="text-blue-700 mb-4">
                If you have any questions about your refund request, please contact our support team.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href={`mailto:support@paymeo.co?subject=Question about Refund Case ${refundData.case_id}`}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Email Support
                </a>
                <a
                  href="/contact"
                  className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                >
                  Send a Message
                </a>
                <button
                  onClick={() => navigate('/request-refund')}
                  className="px-4 py-2 border border-gray-600 text-gray-600 rounded-lg hover:bg-white transition-colors"
                >
                  Submit New Request
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!refundData && !isLoading && !error && caseId && (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold  mb-2">Refund Request Not Found</h3>
            <p className="text-gray-600">
              We couldn't find a refund request with ID: <strong>{caseId}</strong>
            </p>
            <p className="text-gray-600 mt-2">
              Please check your case ID and try again, or contact support if you need assistance.
            </p>
          </div>
        )}
         <SmartsuppChat /> 
      </div>
    </div>
  );
};

export default RefundStatusPage;